<template>
  <Listbox v-model="modelValue">
    <div
      class="relative inline-flex flex-col w-60 text-sm font-medium text-gray-700 group hover:text-gray-900"
    >
      <ListboxButton
        class="relative w-full py-2 pl-3 pr-10 text-left bg-white border rounded-lg shadow-md cursor-default focus:outline-hidden focus-visible:border-clay-600 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm"
      >
        <slot name="selected" :option="modelValue">
          <span class="block truncate">
            {{ isPrimitive(modelValue) ? modelValue : "Select an option" }}
          </span>
        </slot>
        <span
          class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
        >
          <ChevronUpDownIcon aria-hidden="true" class="w-5 h-5 text-gray-400" />
        </span>
      </ListboxButton>

      <transition
        leave-active-class="transition duration-100 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <ListboxOptions
          class="absolute top-full left-0 z-10 mt-1 w-full overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
        >
          <ListboxOption
            v-for="option in options"
            :key="getOptionKey(option)"
            :value="option"
            :disabled="option?.disabled"
            v-slot="{ active, selected, disabled }"
            as="template"
          >
            <li
              :class="[
                disabled
                  ? 'opacity-50 cursor-not-allowed'
                  : active
                  ? 'bg-clay-100 text-clay-900'
                  : 'text-gray-900',
                'relative cursor-default select-none py-2 pl-10 pr-4'
              ]"
            >
              <span
                :class="[
                  selected ? 'font-bold' : 'font-normal',
                  'block truncate'
                ]"
                ><slot
                  name="option"
                  :option="option"
                  :selected="selected"
                  :disabled="disabled"
                >
                  {{ isPrimitive(option) ? option : "" }}
                </slot>
              </span>

              <span
                v-if="selected && !disabled"
                class="absolute inset-y-0 left-0 flex items-center pl-3 text-clay-800"
              >
                <CheckIcon aria-hidden="true" class="w-5 h-5" />
              </span>
            </li>
          </ListboxOption>
        </ListboxOptions>
      </transition>
    </div>
  </Listbox>
</template>

<script setup lang="ts">
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions
} from "@headlessui/vue";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/vue/20/solid";

// Props
defineProps<{
  options: any[];
}>();

const modelValue = defineModel<any>();

// Utility: primitive check
function isPrimitive(value: any): value is string | number {
  return typeof value === "string" || typeof value === "number";
}

// Utility: fallback key
function getOptionKey(option: any): string | number {
  return isPrimitive(option) ? option : JSON.stringify(option);
}
</script>
